import type { ReactNode } from 'react';
import { Card } from '../../../molecules/Card/Card';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { KPIText } from '../../../molecules/KPIText/KPIText';
import { LoadingState } from '../../../molecules/LoadingState/LoadingState';
import { TableHeader } from '../../../molecules/TableHeader/TableHeader';
import { PlatformIcon } from '../../../atoms/PlatformIcon/PlatformIcon';
import { ProgressBar } from '../../../atoms/ProgressBar/ProgressBar';
import { Button } from '../../../atoms/Button/Button';
import { Icon } from '../../../atoms/Icon/Icon';
import { DonutChart } from '../../../organisms/DonutChart/DonutChart';
import { Table, type TableColumn, type TableRowData } from '../../../organisms/Table/Table';
import { seriesColor, type ChartSeriesToken } from '../../../molecules/ChartFrame/chartTheme';
import styles from './InfluencerDetailWorkspace.module.css';

export type InfluencerFigureView = { title: string; value: string; help: string };
export type InfluencerSliceView = { name: string; value: number };

export type InfluencerPlatformView = {
  channel: string;
  channelLabel: string;
  followers: string;
  posts: string;
  engagement: string;
  reactions: string;
  comments: string;
  safeShare: number | null;
};

export type InfluencerIndustryView = {
  id: string;
  label: string;
  posts: string;
  comments: string;
  replies: string;
  safeShare: number | null;
  href: string;
};

export type InfluencerProfileView = {
  name: string;
  figures: InfluencerFigureView[];
  platforms: InfluencerPlatformView[];
  industries: InfluencerIndustryView[];
  reactions: InfluencerSliceView[];
  reactionTotal: number;
  reactionTotalLabel: string;
  reactionKpis: KPIMetric[];
  safety: InfluencerSliceView[];
  safeShare: number | null;
  safeShareLabel: string;
  safetyKpis: KPIMetric[];
};

export type InfluencerIndustryDetailView = {
  influencerName: string;
  industryLabel: string;
  figures: InfluencerFigureView[];
  activity: number;
  safety: InfluencerSliceView[];
  safeShare: number | null;
  safeShareLabel: string;
  safetyKpis: KPIMetric[];
  sentiment: InfluencerSliceView[];
  sentimentTotal: number;
  positiveShareLabel: string;
  sentimentKpis: KPIMetric[];
};

export type InfluencerWorkspaceStatus = 'loading' | 'ready' | 'error' | 'notConnected';

const PLOT_HEIGHT = 224;
const SAFETY_TOKENS: ChartSeriesToken[] = ['chart-4', 'chart-5', 'chart-6'];
const SAFETY_COLORS = SAFETY_TOKENS.map((token, index) => seriesColor(index, token));
const SAFETY_LEGEND = ['Safe', 'Needs review', 'Risky'].map((label, index) => ({
  label,
  color: SAFETY_COLORS[index],
}));

function State({ status, onRetry }: { status: InfluencerWorkspaceStatus; onRetry?: () => void }) {
  if (status === 'loading') {
    return (
      <div className={styles.state} aria-busy="true">
        <LoadingState pattern="orbit" tone="accent" showLabel={false} showElapsed={false} />
      </div>
    );
  }
  return (
    <div className={styles.state} role="alert">
      <p>
        {status === 'notConnected'
          ? 'Live data for this page is not connected yet.'
          : 'Couldn’t load this influencer.'}
      </p>
      {status === 'error' && onRetry && (
        <Button hierarchy="secondary" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

function FigureRow({ figures, columns }: { figures: InfluencerFigureView[]; columns: 4 | 6 }) {
  return (
    <div className={[styles.figures, columns === 6 ? styles.figures6 : styles.figures4].join(' ')}>
      {figures.map((figure) => (
        <Card
          key={figure.title}
          title={figure.title}
          helpText={figure.help}
          kpi={
            <span className={styles.figure}>
              <KPIText>{figure.value}</KPIText>
            </span>
          }
        />
      ))}
    </div>
  );
}

const PLATFORM_COLUMNS: TableColumn[] = [
  { key: 'platform', header: 'Platforms', cellType: 'actionIcons', width: '12%' },
  { key: 'followers', header: 'Followers', width: '16%' },
  { key: 'posts', header: 'Total posts', width: '14%' },
  { key: 'engagement', header: 'Engagement', width: '14%' },
  { key: 'reactions', header: 'Reactions', width: '12%' },
  { key: 'comments', header: 'Comments', width: '12%' },
  { key: 'safety', header: 'Brand safety', cellType: 'progressBar', width: '20%' },
];

const INDUSTRY_COLUMNS: TableColumn[] = [
  { key: 'industry', header: 'Industry', width: '20%' },
  { key: 'posts', header: 'Total posts', width: '15%' },
  { key: 'comments', header: 'Total comments', width: '17%' },
  { key: 'replies', header: 'Total replies', width: '15%' },
  { key: 'safety', header: 'Brand safety', cellType: 'progressBar', width: '21%' },
  { key: 'actions', cellType: 'actionButtons', width: '12%' },
];

export function InfluencerDetailWorkspace({
  status,
  view,
  onRetry,
  children,
}: {
  status: InfluencerWorkspaceStatus;
  view?: InfluencerProfileView;
  onRetry?: () => void;
  children?: ReactNode;
}) {
  if (status !== 'ready' || !view) return <State status={status} onRetry={onRetry} />;

  const platformRows: TableRowData[] = view.platforms.map((row) => ({
    key: row.channel,
    cells: {
      platform: {
        content: (
          <span className={styles.platforms}>
            <PlatformIcon name={row.channel as never} label={row.channelLabel} />
          </span>
        ),
      },
      followers: { text: row.followers },
      posts: { text: row.posts },
      engagement: { text: row.engagement },
      reactions: { text: row.reactions },
      comments: { text: row.comments },
      safety: {
        content:
          row.safeShare === null ? undefined : (
            <ProgressBar
              value={row.safeShare}
              size="sm"
              showLabel
              label={`Safe posts on ${row.channelLabel}`}
            />
          ),
        text: row.safeShare === null ? '—' : undefined,
      },
    },
  }));

  const industryRows: TableRowData[] = view.industries.map((row) => ({
    key: row.id,
    cells: {
      industry: { text: row.label },
      posts: { text: row.posts },
      comments: { text: row.comments },
      replies: { text: row.replies },
      safety: {
        content:
          row.safeShare === null ? undefined : (
            <ProgressBar
              value={row.safeShare}
              size="sm"
              showLabel
              label={`Safe posts in ${row.label}`}
            />
          ),
        text: row.safeShare === null ? '—' : undefined,
      },
      actions: {
        content: (
          <Button
            href={row.href}
            hierarchy="ghostColor"
            iconTrailing={<Icon name="chevronRight" />}
          >
            View details
          </Button>
        ),
      },
    },
  }));

  return (
    <div className={styles.page}>
      <FigureRow figures={view.figures} columns={6} />
      <Table
        label="Platform analytics"
        columns={PLATFORM_COLUMNS}
        rows={platformRows}
        header={
          <TableHeader
            title="Platform analytics"
            helpText="How this creator performs on each platform they post on."
            helpSupportingText="Followers are the creator's own total, repeated per platform — the roster carries one figure per person, not one per channel."
          />
        }
      />
      <div className={styles.charts}>
        <Card
          state={view.reactionTotal > 0 ? 'default' : 'empty'}
          title="Audience reactions"
          emptyLabel="No engagement in this period"
          helpText="What their audience actually did with this creator's posts."
          kpi={<KPI metrics={view.reactionKpis} />}
          chart={
            <DonutChart
              data={view.reactions}
              nameKey="name"
              valueKey="value"
              height={PLOT_HEIGHT}
              legend="none"
              centerLabel="total engagement"
              centerValue={view.reactionTotalLabel}
            />
          }
          legend={
            <ChartLegend
              items={view.reactions.map((slice, index) => ({
                label: slice.name,
                color: seriesColor(index),
              }))}
            />
          }
        />
        <Card
          state={
            view.safety.reduce((total, slice) => total + slice.value, 0) > 0 ? 'default' : 'empty'
          }
          title="Brand safety score"
          emptyLabel="Nothing posted in this period"
          helpText="How safely this creator posts, as the mix of their posts in the selected period."
          kpi={<KPI metrics={view.safetyKpis} />}
          chart={
            <DonutChart
              data={view.safety}
              nameKey="name"
              valueKey="value"
              height={PLOT_HEIGHT}
              legend="none"
              centerLabel="safe this period"
              centerValue={view.safeShareLabel}
              colors={SAFETY_TOKENS}
            />
          }
          legend={<ChartLegend items={SAFETY_LEGEND} />}
        />
      </div>
      <Table
        label="Industry specific split"
        columns={INDUSTRY_COLUMNS}
        rows={industryRows}
        header={
          <TableHeader
            title="Industry specific split"
            helpText="Which industries this creator posts about, and how safely."
          />
        }
      />
      {children}
    </div>
  );
}

export function InfluencerIndustryWorkspace({
  status,
  view,
  onRetry,
  children,
}: {
  status: InfluencerWorkspaceStatus;
  view?: InfluencerIndustryDetailView;
  onRetry?: () => void;
  children?: ReactNode;
}) {
  if (status !== 'ready' || !view) return <State status={status} onRetry={onRetry} />;
  const sentimentTokens: ChartSeriesToken[] = ['chart-4', 'chart-5', 'chart-6'];
  return (
    <div className={styles.page}>
      <FigureRow figures={view.figures} columns={4} />
      <div className={styles.charts}>
        <Card
          state={view.activity > 0 ? 'default' : 'empty'}
          title="Brand safety score"
          emptyLabel={`Nothing posted about ${view.industryLabel} in this period`}
          helpText="How safely this creator posts about this industry."
          kpi={<KPI metrics={view.safetyKpis} />}
          chart={
            <DonutChart
              data={view.safety}
              nameKey="name"
              valueKey="value"
              height={PLOT_HEIGHT}
              legend="none"
              centerLabel={`safe in ${view.industryLabel}`}
              centerValue={view.safeShareLabel}
              colors={SAFETY_TOKENS}
            />
          }
          legend={<ChartLegend items={SAFETY_LEGEND} />}
        />
        <Card
          state={view.sentimentTotal > 0 ? 'default' : 'empty'}
          title="Sentiment breakdown"
          emptyLabel={`Nothing posted about ${view.industryLabel} in this period`}
          helpText={`How audiences responded to ${view.influencerName}’s ${view.industryLabel} posts.`}
          kpi={<KPI metrics={view.sentimentKpis} />}
          chart={
            <DonutChart
              data={view.sentiment}
              nameKey="name"
              valueKey="value"
              height={PLOT_HEIGHT}
              legend="none"
              centerLabel="positive share"
              centerValue={view.positiveShareLabel}
              colors={sentimentTokens}
            />
          }
          legend={
            <ChartLegend
              items={view.sentiment.map((slice, index) => ({
                label: slice.name,
                color: seriesColor(index, sentimentTokens[index]),
              }))}
            />
          }
        />
      </div>
      {children}
    </div>
  );
}
